"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { MAX_ORDER_FLOORS } from "@/lib/orders/floorLimits";
import { PricingConfig, calculatePrice, formatPrice } from "@/lib/pricing";
import { useTranslations } from "next-intl";
import { ComponentProps, useEffect, useState } from "react";
import { Checkbox } from "../form";
import TitleH3 from "./fonts/TitleH3";
import MainLink from "./navigation/MainLink";

type PriceCalculatorProps = ComponentProps<"section"> & {
  paidOptions: {
    label: string;
    name: string;
  }[];
  freeOptions?: {
    label: string;
    name: string;
  }[];
  pricingConfig: PricingConfig;
  orderHref: ComponentProps<typeof MainLink>["href"];
};

const PriceCalculator = ({ ...props }: PriceCalculatorProps) => {
  const [floorSliderValue, setFloorSliderValue] = useState([1]);
  const [floorInputValue, setFloorInputValue] = useState("1");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [calculatedPrice, setCalculatedPrice] = useState("0,00");

  const t = useTranslations("common");

  const clampFloors = (value: number) =>
    Math.min(Math.max(1, Math.trunc(value)), MAX_ORDER_FLOORS);

  // Sync floorInputValue to sliderValue
  useEffect(() => {
    const num = Number(floorInputValue);
    if (!isNaN(num)) {
      const next = clampFloors(num);
      if (next !== floorSliderValue[0]) {
        setFloorSliderValue([next]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorInputValue]);

  // Sync floorSliderValue to inputValue
  useEffect(() => {
    const str = floorSliderValue[0].toString();
    if (str !== floorInputValue) {
      setFloorInputValue(str);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorSliderValue]);

  // Calculate price whenever floors or options change
  useEffect(() => {
    const priceCalc = calculatePrice(
      props.pricingConfig,
      floorSliderValue[0],
      selectedOptions,
    );
    setCalculatedPrice(formatPrice(priceCalc.total));
  }, [floorSliderValue, selectedOptions, props.pricingConfig]);

  // Handle option checkbox changes
  const handleOptionChange = (
    optionName: string,
    checked: boolean | string,
  ) => {
    const isChecked = typeof checked === "boolean" ? checked : checked === "on";
    setSelectedOptions((prev) => {
      if (isChecked) {
        return [...prev, optionName];
      } else {
        return prev.filter((name) => name !== optionName);
      }
    });
  };

  return (
    <section
      className="flex flex-col items-center gap-5 px-4 py-16"
      id={props.id}
    >
      <Card className="h-full w-full max-w-3xl">
        <CardHeader>
          <CardTitle className="text-center">
            <TitleH3 title={t("priceCalculator")} />
          </CardTitle>

          <CardDescription className="mt-5 flex flex-col items-center gap-7">
            <div className="flex flex-col items-center gap-5">
              <label className="flex items-center gap-3 text-lg">
                {t("floors")}:
                <input
                  name="floors"
                  type="number"
                  min={1}
                  max={MAX_ORDER_FLOORS}
                  className="h-9 w-22 border pl-1 outline-0"
                  value={floorInputValue}
                  onChange={(e) => setFloorInputValue(e.target.value)}
                />
              </label>
              <Slider
                defaultValue={[1]}
                min={1}
                max={MAX_ORDER_FLOORS}
                step={1}
                value={floorSliderValue}
                onValueChange={setFloorSliderValue}
              />

              <span className="text-ring text-center text-sm">
                {t("priceCalcInfo")}
              </span>
            </div>

            <div className="flex w-full flex-col items-center gap-2 px-4 text-center sm:px-10">
              <span className="text-ring text-sm">
                €{" "}
                <span
                  className="text-primary text-5xl font-semibold sm:text-7xl"
                  data-testid="price-calculator-total"
                >
                  {calculatedPrice}
                </span>
              </span>
              <span className="text-ring text-center text-sm">
                {t("include")} 19% {t("tax")}
              </span>
              <Separator className="bg-primary" />
            </div>
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-5 px-6 sm:px-10 md:px-16">
          <span className="text-primary text-center text-xl">
            {t("paidOptions")}
          </span>
          <div className="text-ring grid grid-cols-1 gap-2 sm:grid-cols-2">
            {props.paidOptions.map((option, i) => {
              return (
                <Checkbox
                  key={i}
                  label={option.label}
                  name={option.name}
                  onCheckedChange={(checked) =>
                    handleOptionChange(option.name, checked)
                  }
                />
              );
            })}
          </div>

          {props.freeOptions && (
            <>
              <span className="text-primary text-center text-xl">
                {t("freeOptions")}
              </span>
              <div className="text-ring grid grid-cols-1 gap-2 sm:grid-cols-2">
                {props.freeOptions?.map((option, i) => {
                  return (
                    <Checkbox
                      key={i}
                      label={option.label}
                      name={option.name}
                      defaultChecked
                      disabled
                    />
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <MainLink href={props.orderHref} title={t("nowOrder")} />
        </CardFooter>
      </Card>
    </section>
  );
};

export default PriceCalculator;
